'use client'

/*
 * ReactionTimeTest
 *
 * Click when the panel turns green. Five trials, scored on the average.
 *
 * THE DESIGN IS THE PRODUCT HERE. Digit span can look like an instrument
 * because the task is effortful and the effort holds attention. Reaction time
 * is over in a quarter of a second, so if the screen is not doing the work
 * nothing is. Hence the full-bleed colour flood: red fills the panel while you
 * wait, green detonates, and the number lands on the same surface you just hit.
 * A small grey readout under a form label would be technically identical and
 * completely dead.
 *
 * WHAT IS HONEST ABOUT THE NUMBERS:
 * A browser cannot measure reaction time the way a lab can. Between the display
 * refreshing and the click registering there is monitor latency, input latency
 * and event loop delay, which together usually add somewhere between 30 and 80
 * milliseconds. Every result here carries that overhead, which is why the page
 * says so and why the comparison is against other people doing the same thing
 * in a browser rather than against published laboratory norms.
 *
 * Trials that are clicked before green are not scored and do not silently
 * inflate the average. They are replayed, because a jump is a failure of
 * inhibition rather than a fast reaction, and averaging it in would reward it.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import Link from 'next/link'
import { Zap, RotateCcw, ArrowRight, MousePointerClick } from 'lucide-react'
import { normalCDF } from '@/lib/iqStats'
import TestPromptPopup from '@/components/TestPromptPopup'

const TRIALS = 5
const MIN_WAIT = 1200
const MAX_WAIT = 4500

// Browser-measured reaction time across large public datasets clusters a little
// above a quarter of a second, well slower than lab figures because of the
// display and input latency described above. These are the parameters the
// percentile is computed from, and the page is explicit that they describe
// people clicking in browsers, not a normative sample.
const RT_MEAN = 273
const RT_SD = 45

// Lower is better, so the percentile is inverted against the normal curve.
function percentileForRt(ms) {
  return (1 - normalCDF((ms - RT_MEAN) / RT_SD)) * 100
}

// Tiers exist because "284ms" means nothing on its own and a label is what
// people screenshot. The thresholds are round numbers on purpose: this is a
// readout, not a measurement, and false precision would oversell it.
// NO TIER IS GREY. The first version gave Average a slate gradient, which meant
// the single most likely outcome, the band most people land in, rendered the
// payoff screen in grey. The modal result has to look like something worth
// seeing or the whole screen reads as a shrug. Slower tiers get warmer colours
// rather than duller ones.
const TIERS = [
  { max: 200, label: 'Lightning', sub: 'Faster than almost anyone', ring: 'from-fuchsia-500 to-purple-600', chip: 'bg-fuchsia-100 text-fuchsia-800' },
  { max: 240, label: 'Very fast', sub: 'Comfortably quicker than most', ring: 'from-cyan-500 to-blue-600', chip: 'bg-cyan-100 text-cyan-800' },
  { max: 285, label: 'Quick', sub: 'Above the typical browser result', ring: 'from-emerald-500 to-teal-600', chip: 'bg-emerald-100 text-emerald-800' },
  { max: 330, label: 'Average', sub: 'Right around where most people land', ring: 'from-indigo-500 to-violet-600', chip: 'bg-indigo-100 text-indigo-800' },
  { max: 400, label: 'Relaxed', sub: 'A touch slower than typical', ring: 'from-amber-500 to-orange-600', chip: 'bg-amber-100 text-amber-800' },
  { max: Infinity, label: 'Unhurried', sub: 'Worth another go when you are settled', ring: 'from-rose-500 to-pink-600', chip: 'bg-rose-100 text-rose-800' },
]

const tierFor = (ms) => TIERS.find((t) => ms < t.max)

export default function ReactionTimeTest() {
  const [phase, setPhase] = useState('idle') // idle | waiting | go | scored | early | finished
  const [times, setTimes] = useState([])
  const [lastTime, setLastTime] = useState(null)
  const [best, setBest] = useState(null)
  const [isNewBest, setIsNewBest] = useState(false)

  const [countUp, setCountUp] = useState(0)

  const goAt = useRef(0)
  const timer = useRef(null)

  useEffect(() => () => clearTimeout(timer.current), [])

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem('biqt_reaction_best')
      setBest(stored ? parseInt(stored, 10) : null)
    } catch {
      setBest(null)
    }
  }, [])

  const armTrial = useCallback(() => {
    setPhase('waiting')
    clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      goAt.current = performance.now()
      setPhase('go')
    }, MIN_WAIT + Math.random() * (MAX_WAIT - MIN_WAIT))
  }, [])

  const start = () => {
    setTimes([])
    setLastTime(null)
    setIsNewBest(false)
    armTrial()
  }

  const handleHit = () => {
    if (phase === 'idle' || phase === 'finished') { start(); return }

    // Clicked before green. Not scored, and the trial is replayed.
    if (phase === 'waiting') {
      clearTimeout(timer.current)
      setPhase('early')
      return
    }

    if (phase === 'early' || phase === 'scored') { armTrial(); return }

    if (phase === 'go') {
      const ms = Math.round(performance.now() - goAt.current)
      const next = [...times, ms]
      setLastTime(ms)
      setTimes(next)

      if (next.length >= TRIALS) {
        const avg = Math.round(next.reduce((a, b) => a + b, 0) / next.length)
        if (best === null || avg < best) {
          setIsNewBest(best !== null)
          setBest(avg)
          try { window.localStorage.setItem('biqt_reaction_best', String(avg)) } catch {}
        }
        setPhase('finished')
      } else {
        setPhase('scored')
      }
    }
  }

  const average = times.length ? Math.round(times.reduce((a, b) => a + b, 0) / times.length) : 0

  // The average counts up rather than appearing. It is the payoff, and a number
  // that lands instantly gets read once and forgotten.
  useEffect(() => {
    if (phase !== 'finished' || !average) { setCountUp(0); return }
    let frame
    const started = performance.now()
    const tick = (now) => {
      const t = Math.min(1, (now - started) / 700)
      setCountUp(Math.round((1 - Math.pow(1 - t, 3)) * average))
      if (t < 1) frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [phase, average])
  const tier = average ? tierFor(average) : TIERS[3]
  const percentile = average ? percentileForRt(average) : 50

  // Panel styling per phase. The colour IS the interface.
  const panel = {
    idle: 'bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600',
    waiting: 'bg-gradient-to-br from-rose-500 to-red-600',
    go: 'bg-gradient-to-br from-emerald-400 to-green-500',
    scored: 'bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600',
    early: 'bg-gradient-to-br from-amber-500 to-orange-600',
    finished: `bg-gradient-to-br ${tier.ring}`,
  }[phase]

  const interactive = phase !== 'finished'

  return (
    <div className="bg-white border border-gray-200 rounded-3xl shadow-xl overflow-hidden">
      <div
        onClick={interactive ? handleHit : undefined}
        onKeyDown={interactive ? (e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); handleHit() } } : undefined}
        role={interactive ? 'button' : undefined}
        tabIndex={interactive ? 0 : undefined}
        aria-label={interactive ? 'Reaction test area. Click or press space when the panel turns green.' : undefined}
        className={`${panel} select-none transition-colors duration-75 ${interactive ? 'cursor-pointer' : ''} min-h-[340px] sm:min-h-[400px] flex flex-col items-center justify-center text-center px-6 py-10`}
      >
        {phase === 'idle' && (
          <>
            <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mb-5">
              <MousePointerClick className="w-8 h-8 text-white" />
            </div>
            <p className="text-3xl sm:text-4xl font-bold text-white mb-3">Click to start</p>
            <p className="text-blue-100 max-w-xs leading-relaxed">
              Wait for the panel to turn green, then click as fast as you can.
              Five goes.
            </p>
          </>
        )}

        {phase === 'waiting' && (
          <>
            <div className="flex gap-2 mb-6">
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className="w-3 h-3 rounded-full bg-white/70 animate-pulse"
                  style={{ animationDelay: `${i * 160}ms` }}
                />
              ))}
            </div>
            <p className="text-4xl sm:text-5xl font-bold text-white mb-2">Wait...</p>
            <p className="text-red-100">Click the moment it turns green</p>
          </>
        )}

        {phase === 'go' && (
          <>
            <Zap className="w-16 h-16 text-white mb-3 fill-current" />
            <p className="text-6xl sm:text-7xl font-bold text-white tracking-tight">CLICK!</p>
          </>
        )}

        {phase === 'scored' && (
          <>
            <p className="text-7xl sm:text-8xl font-bold text-white tabular-nums leading-none">
              {lastTime}
              <span className="text-2xl sm:text-3xl font-semibold text-blue-200 ml-1">ms</span>
            </p>
            <p className="text-blue-100 mt-4">
              {TRIALS - times.length} to go. Click to continue.
            </p>
          </>
        )}

        {phase === 'early' && (
          <>
            <p className="text-4xl sm:text-5xl font-bold text-white mb-3">Too soon</p>
            <p className="text-orange-100 max-w-xs">
              You clicked before it turned green. That one does not count. Click
              to go again.
            </p>
          </>
        )}

        {phase === 'finished' && (
          <div className="w-full">
            {isNewBest && (
              <span className="inline-block px-3 py-1 rounded-full bg-white/25 backdrop-blur-sm text-white text-xs font-bold tracking-wide mb-4">
                NEW PERSONAL BEST
              </span>
            )}

            <p className="text-xs font-semibold text-white/70 uppercase tracking-[0.2em] mb-2">
              Average of {TRIALS}
            </p>

            <p className="text-7xl sm:text-8xl font-bold tabular-nums leading-none text-white drop-shadow-sm">
              {countUp}
              <span className="text-3xl font-semibold text-white/60 ml-1">ms</span>
            </p>

            <p className="text-3xl sm:text-4xl font-bold text-white mt-4 tracking-tight">
              {tier.label}
            </p>
            <p className="text-white/80 mt-1">{tier.sub}</p>

            <span className="inline-block mt-4 px-4 py-1.5 rounded-full bg-white/20 backdrop-blur-sm text-white text-sm font-bold tabular-nums">
              Faster than {Math.round(Math.max(1, Math.min(99, percentile)))}% of people
            </span>

            {/* Trial times as bars rather than a row of grey chips. Five bars
                of different heights is a shape you read instantly, and it shows
                consistency, which the average alone hides. */}
            <div className="flex items-end justify-center gap-2 mt-7 h-16">
              {times.map((t, i) => {
                const slowest = Math.max(...times)
                const height = Math.max(18, (t / slowest) * 100)
                const fastest = t === Math.min(...times)
                return (
                  <div key={i} className="flex flex-col items-center justify-end h-full">
                    <span className="text-[10px] font-bold text-white/80 tabular-nums mb-1">{t}</span>
                    <div
                      className={`w-8 rounded-t-md transition-all duration-500 ${fastest ? 'bg-white' : 'bg-white/40'}`}
                      style={{ height: `${height}%` }}
                    />
                  </div>
                )
              })}
            </div>
            <p className="text-[11px] text-white/60 mt-2">
              your five trials, fastest highlighted
            </p>
          </div>
        )}
      </div>

      {/* Trial progress, outside the panel so it survives every colour change */}
      {phase !== 'finished' && (
        <div className="flex items-center justify-center gap-2 py-4 border-t border-gray-100">
          {Array.from({ length: TRIALS }).map((_, i) => (
            <span
              key={i}
              className={`h-2 rounded-full transition-all ${
                i < times.length ? 'w-8 bg-blue-600' : 'w-2 bg-gray-200'
              }`}
            />
          ))}
        </div>
      )}

      {phase === 'finished' && (
        <div className="p-6 sm:p-8">
          {/* Straight under the number, before anything explanatory. */}
          <Link
            href="/test"
            className="group relative block w-full overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600 bg-[length:200%_100%] hover:bg-[position:100%_0] p-[3px] shadow-xl shadow-purple-500/25 transition-[background-position] duration-700"
          >
            <span className="block rounded-[13px] bg-white px-6 py-5 text-center transition-colors group-hover:bg-transparent">
              <span className="block text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent group-hover:text-white transition-colors">
                Now find your actual IQ
              </span>
              <span className="mt-1 flex items-center justify-center text-sm font-medium text-gray-500 group-hover:text-blue-100 transition-colors">
                50 questions · all four domains · instant score
                <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-1 transition-transform" />
              </span>
            </span>
          </Link>

          <p className="text-sm text-gray-600 text-center leading-relaxed mt-5">
            Speed is real, and it is the smallest part of the picture. Processing
            speed correlates with cognitive ability, but reasoning is what a
            score is mostly made of.
          </p>

          <div className="flex items-center justify-center gap-5 mt-5">
            <button
              type="button"
              onClick={start}
              className="inline-flex items-center text-gray-600 hover:text-gray-900 font-semibold text-sm transition-colors"
            >
              <RotateCcw className="w-4 h-4 mr-1.5" />
              Go again
            </button>
            {best !== null && (
              <span className="text-sm text-gray-400 tabular-nums">Best: {best}ms</span>
            )}
          </div>

          <p className="text-xs text-gray-400 mt-5 text-center leading-relaxed">
            Measured in a browser, so your monitor, mouse and machine add
            somewhere between 30 and 80ms that a lab setup would not. Compare
            yourself with other people clicking in browsers, not with published
            laboratory figures.
          </p>
        </div>
      )}

      {/* Mounted only on the result, so its timer starts when the score lands.
          Mid-run would be worse here than on digit span: the task is reacting
          to the screen changing, so a modal appearing mid-trial is itself a
          false signal and would wreck the trial it interrupted. */}
      {phase === 'finished' && (
        <TestPromptPopup
          delayMs={4000}
          storageKey="biqt_reaction_prompt"
          headline="Fast hands. What about the rest?"
          body={
            <>
              Reaction time is processing speed, the smallest piece of a
              cognitive profile. Our 50-question assessment measures verbal,
              numerical, spatial and memory reasoning, and gives you a
              full-scale score with your exact percentile.
            </>
          }
        />
      )}
    </div>
  )
}
